import DropdownBtn from './DropdownBtn';
import DropdownLayer from './DropdownLayer';
import DropdownSelect from './DropdownSelect';
import DropdownStore from './DropdownStore';
import DropdownInstControl from './DropdownInstControl';
import { validateOption, findMaxDepth, setNewData, getActiveKeyFromValueArr, getDefaultActiveKey, getIsPc, setActiveDataToObject } from './util';
import { IDefaultOption, IDropdownConfig, OptionType, StateType, ActiveDataObjType, IItem } from '../interface/IDropdown';
export default class Dropdown {
    private dropdownBtn: DropdownBtn | null = null;
    private dropdownLayer: DropdownLayer | null = null;
    private dropdownSelect: DropdownSelect | null = null;
    private isPc: boolean = getIsPc();
    private option: OptionType;
    private store: DropdownStore = new DropdownStore();

    constructor(config: IDropdownConfig) {
        const defaultOption: IDefaultOption = {
            name: null,
            btnType: 'no-border',
            btnShowDepthLength: 0,
            layerType: 'basic',
            useSelectExceptPc: false,
            useFitModeExceptPc: false,
            isInitialActive: false,
            isInitialCallbackCall: false,
            changeCallback: null
        };

        this.option = {...defaultOption, ...config};

        if(!validateOption(this.option)) {
            return;
        }

        this.init();
        DropdownInstControl.addInstance(this); // 열려있는 다른 Dropdown 닫기 위해, Dropdown instance 생성 시 array에 담아둠
	}

    private init() {
        const maxDepth = findMaxDepth(this.option.data); // 최대 뎁스 구하기
        const newData = setNewData(this.option.data, maxDepth); // 데이터 가공

        this.option.parent.classList.add('ncui-dropdown-wrap');
        if(this.option.name) {
            this.option.parent.setAttribute('dropdown-name', this.option.name);
        }
        if(this.option.useFitModeExceptPc) {
            this.option.parent.classList.add('fit-mode');
        }
        this.store.dispatch<'maxDepth', StateType['maxDepth']>('maxDepth', maxDepth);
        this.store.dispatch<'newData', StateType['newData']>('newData', newData);
        
        // 버튼 생성
        this.dropdownBtn = new DropdownBtn({
            ...this.option,
            store: this.store
        });

        // 레이어 생성
        this.dropdownLayer = new DropdownLayer({
            ...this.option,
            data: this.store.state.newData,
            store: this.store
        });

        // Tablet, mobile일 때 select 사용 및 데이터 뎁스가 최대 1뎁스일 때 select 사용 가능
        if(this.option.useSelectExceptPc && this.store.state.maxDepth === 1 && !this.isPc) {
            this.dropdownSelect = new DropdownSelect({
                ...this.option,
                data: this.store.state.newData[0]!,
                store: this.store
            });
        }

        // 초기 로딩 시, 첫번째 요소 활성화할 때
        if(this.option.isInitialActive) {
            this.activeKey(getDefaultActiveKey(this.store.state.maxDepth));
        }
    }

    get isOpen(): boolean {
        return this.store.state.isOpen;
    }

    get activeData(): IItem[] {
        return this.store.state.activeData;
    }

    public openLayer() {
        DropdownInstControl.closeOpenDropdown(); // 열려있는 다른 Dropdown 닫음
        this.store.dispatch<'isOpen', StateType['isOpen']>('isOpen', true);
    }

    public closeLayer() {
        this.store.dispatch<'isOpen', StateType['isOpen']>('isOpen', false);
    }

    /*
        외부에서 호출 - key값으로 활성화 (depth1Index-depth2Index-..., ex. 1-0-0)
        @param {string} key 활성화하려는 마지막 요소의 키값
        @return {ActiveDataType | void} this.activeData 현재 활성화된 전체 뎁스의 데이터
    */
    public activeKey(key: string): ActiveDataObjType {
        this.store.dispatch<'prevActiveKey', StateType['prevActiveKey']>('prevActiveKey', this.store.state.activeKey);
        this.store.dispatch<'activeKey', StateType['activeKey']>('activeKey', key);

        const { activeData, isLastDepth, isInitial } = this.store.state;
        const isCallbackCall = ((isInitial && this.option.isInitialCallbackCall) || !isInitial);
        const isNotSelectbox = !this.option.useSelectExceptPc || (this.option.useSelectExceptPc && this.isPc);
        
        // 초기 실행 시 콜백 실행 막을 수 있도록 옵션 추가 및 selectbox일 때는 DropdownSelectbox에서 콜백 실행 처리
        if(isCallbackCall && isNotSelectbox && isLastDepth) {
            typeof this.option.changeCallback === 'function' && this.option.changeCallback(setActiveDataToObject(activeData));
        }

        if(isInitial) {
            this.store.setState('isInitial', false);
        }

        return setActiveDataToObject(activeData);
    }

    /*
        외부에서 호출 - value값으로 활성화 ([depth1Value, depth2Value, ...], ex. ['dropdown1', 'dropdown2'])
        - 단 뎁스별 데이터는 정확해야 함 (뎁스 한군데라도 데이터가 다르면 활성화 안됨)
        @param {string[]} valueArr 활성화하려는 요소의 각 뎁스별 value
        @return {ActiveDataObjType} this.activeData 현재 활성화된 전체 뎁스의 데이터
    */
    public activeValue(valueArr: string[]): ActiveDataObjType{
        const key = getActiveKeyFromValueArr(valueArr, this.store.state.newData);
        
        if(key) {
            return this.activeKey(key);
        }

        if(this.store.state.isInitial) {
            this.store.setState('isInitial', false);
        }

        return {};
    }

    /*
        외부에서 호출 - 완전 초기화
        - 새로운 옵션값 받아서 초기화 가능
        @param {IDropdownConfig} _config 새로운 옵션값
    */
    public reset(config: IDropdownConfig) {
        this.option = {...this.option, ...config};

        if(!validateOption(this.option)) {
            return;
        }

        this.option.parent.innerHTML = '';
        this.store.reset();
        this.init();
    };

    static otherDropdownClose(dropdownInstArr: Dropdown[]) {
        if(!Array.isArray(dropdownInstArr) || dropdownInstArr?.length === 0) {
            return;
        }

        dropdownInstArr.forEach(v => {
            if(v.isOpen) {
                v.closeLayer();
            } 
        });
    }
}
// end of class